import React from 'react';

import { Navigate, Route } from 'react-router-dom';

import {
  DashboardType,
  enableChat,
  getIsUserSignedUp,
  PendingActivationTypes,
  TypeOfUser,
} from '@pharmaplan/common';

import LogoutHandler from '@pharmaplan/common/helpers/LogoutHandler';
import AdminHome from '../components/Admin/Home';
import AdminReports from '../components/Admin/AdminReports';
import AssignAccounts from '../components/Admin/AdminList/AssignAccounts';
import AdminDashboard from '../components/Admin/Dashboard';
import PharmacistList from '../components/Admin/PharmacistList';
import PharmacyChains from '../components/Admin/PharmacyChains';
import SoftwareList from '../components/Admin/SoftwareList';
import PharmacyList from '../components/Admin/PharmacyList';
import IncompatibleMatches from '../components/Admin/IncompatibleMatches';
import AdminConfiguration from '../components/Admin/AdminConfiguration';
import ContactPreferences from '../components/Admin/ContactPreferences';
import PendingActivationList from '../components/Admin/PendingActivationList';
import PendingCancellations from '../components/Admin/PendingCancellations';
import PharmacistCapacities from '../components/Admin/PharmacistCapacities';
import UpdateIncompatibleMatches from '../components/Admin/IncompatibleMatches/UpdateIncompatibleMatches';
import AdminList from '../components/Admin/AdminList';
import { useAppSelector } from '../hooks/useAppSelector';
import useAdmin from '../hooks/useAdmin';
import {
  BroadcastGroupActionType,
  BroadcastTabsType,
  Constants,
  PharmacyAdminActionTypes,
} from '../helpers/Constants';
import CreateNewAdmin from '../components/Admin/AdminList/CreateNewAdmin';
import UpdateAdmin from '../components/Admin/AdminList/CreateNewAdmin/UpdateAdmin';
import ListOfWorkshifts from '../components/Admin/PharmacyList/ListOfWorkshifts';
import DetailedRatings from '../components/Admin/DetailedRatings';
import BroadcastList from '../components/Admin/BroadcastList';
import CreateEditBroadcastGroup from '../components/Admin/BroadcastList/CreateEditBroadcastGroup';
import CreateEditBroadcastNotification from '../components/Admin/BroadcastList/CreateEditBroadcastNotification';

import ProtectedRoute from './ProtectedRoute';
import Logs from '../components/Admin/Logs';
import AdminProfile from '../components/Admin/AdminProfile';
import ChangePassword from '../components/ChangePassword';
import PharmacyAdministrators from '../components/Admin/PharmacyAdministrators';
import CreateEditPharmacyAdministrator from '../components/Admin/PharmacyAdministrators/CreateEditPharmacyAdministrator';
import AppointExistingPharmacy from '../components/Admin/PharmacyAdministrators/AppointExistingPharmacyList/AppointExistingPharmacy';
import PharmaplanStatistics from '../components/Admin/PharmaplanStatistics';
import ChatWindow from '../components/Admin/ChatWindow';
import ProtectedCustomRoute from './DashboardProtectedRoute';
import AdvertisingCampaign from '../components/Admin/AdvertisingCampaign';
import CreateEditAdvertisingCampaign from '../components/Admin/AdvertisingCampaign/CreateEditAdvertisingCampaign';
import Campaigns from '../components/Admin/Campaigns';
import CampaignBookings from '../components/Admin/Campaigns/CampaignBookings';
import Leads from '../components/Admin/Leads';
import CreateEditLead from '../components/Admin/Leads/CreateEditLead';
import Entitlements from '../components/Admin/Entitlements';
import EditEntitlement from '../components/Admin/Entitlements/EditEntitlement';
import PharmagapTransactions from '../components/Admin/PharmagapTransactions';
import PendingRequestsList from '../components/Admin/PendingRequestsList';
import TechniciansList from '../components/Admin/TechniciansList';
import PharmacyStudentList from '../components/Admin/PharmacyStudentList';
import { CampaignBookingType } from '../components/Admin/Campaigns/CampaignBookings/CampaignBookings';
import { LeadTabs } from '../components/Admin/Leads/Leads';
import CampaignStepper from '../components/Admin/Campaigns/CampaignStepper';

const {
  pharmaplanStatistics,
  home,
  adminCalendarDashboard,
  softwareList,
  pharmacistCapacities,
  pharmacistList,
  pharmacyChains,
  dashboard,
  pendingActivationList,
  pendingCancellation,
  administratorList,
  adminConfiguration,
  reports,
  logs,
  contactPreferences,
  pharmacyList,
  incompatibleMatch,
  updateIncompatibleMatch,
  bookingReport,
  pendingRequests,
  requestedReport,
  availabilityReport,
  createNewAdmin,
  updateAdmin,
  assignAccounts,
  postedWorkshiftReport,
  ownershipReport,
  createMultiple,
  createTechnicianMultiple,
  createStudentMultiple,
  listOfWorkshifts,
  viewRatings,
  viewStudentRating,
  viewTechnicianRating,
  pharmacyListIncompatible,
  pharmacyListIncompatibleUpdate,
  broadcastList,
  createBroadcastGroup,
  editBroadcastGroup,
  createNotification,
  editNotification,
  activityLogs,
  workshiftLogs,
  bookingLogs,
  pharmacyAdministrators,
  createPharmacyAdministrators,
  updatePharmacyAdministrators,
  appointPharmacyAdministrators,
  availabilityLogs,
  appointPharmacyList,
  broadcastListNotification,
  leads,
  profile,
  changePassword,
  pendingActivationListPharmacist,
  adminChat,
  advertisingCampaigns,
  createAdvertisingCampaign,
  editAdvertisingCampaign,
  campaigns,
  createCampaign,
  editCampaign,
  configureCampaignEmail,
  campaignBookings,
  leadBookings,
  pharmacistLeads,
  technicianLeads,
  pharmacyLeads,
  linkedLeads,
  studentLeads,
  createLead,
  editLead,
  entitlements,
  editEntitlement,
  pharmacyViewRatings,
  transactions,
  techniciansList,
  pharmacyStudentList,
} = Constants.paths.admin;

const { notifications, groups } = BroadcastTabsType;
const { create, appoint, edit } = PharmacyAdminActionTypes;

const AdminRouter = () => {
  const { isAdmin, isSuperAdmin } = useAdmin();

  const user = !!LogoutHandler.isUserLoggedIn();
  const isUserSignedUp = useAppSelector(getIsUserSignedUp);
  const isChatEnabled = useAppSelector(enableChat);

  const isAuthenticatedUser = user && !isUserSignedUp;

  const isAuthenticatedAdmin = isAdmin && isAuthenticatedUser;
  return (
    <Route
      path={home}
      element={<ProtectedRoute authenticated={isAuthenticatedAdmin} />}
    >
      <Route path={home} element={<AdminHome />}>
        <Route
          path={home}
          element={<Navigate replace to={adminCalendarDashboard} />}
        />
        <Route
          path={dashboard}
          element={<Navigate replace to={adminCalendarDashboard} />}
        />
        <Route path={dashboard} element={<AdminDashboard />}>
          <Route path={DashboardType.calendar} element={<AdminDashboard />} />
          <Route path={DashboardType.map} element={<AdminDashboard />} />
        </Route>
        <Route path={createMultiple} element={<AdminDashboard />} />
        <Route path={createTechnicianMultiple} element={<AdminDashboard />} />
        <Route path={createStudentMultiple} element={<AdminDashboard />} />

        <Route path={softwareList} element={<SoftwareList />} />
        <Route
          path={pendingActivationList}
          element={<Navigate replace to={pendingActivationListPharmacist} />}
        />
        <Route path={pendingActivationList} element={<PendingActivationList />}>
          <Route
            path={TypeOfUser.pharmacist}
            element={<PendingActivationList />}
          />
          <Route
            path={TypeOfUser.pharmacy}
            element={<PendingActivationList />}
          />
          <Route
            path={TypeOfUser.technician}
            element={<PendingActivationList />}
          />
          <Route
            path={TypeOfUser.student}
            element={<PendingActivationList />}
          />
          <Route
            path={PendingActivationTypes.deletedUsers}
            element={<PendingActivationList />}
          />
        </Route>
        <Route path={pharmaplanStatistics} element={<PharmaplanStatistics />} />
        <Route path={pendingCancellation} element={<PendingCancellations />} />
        <Route path={pharmacistCapacities} element={<PharmacistCapacities />} />
        <Route path={contactPreferences} element={<ContactPreferences />} />
        <Route path={pharmacyChains} element={<PharmacyChains />} />
        <Route path={administratorList} element={<AdminList />} />
        <Route
          path={reports}
          element={<Navigate replace to={bookingReport} />}
        />

        <Route path={bookingReport} element={<AdminReports />} />
        <Route path={availabilityReport} element={<AdminReports />} />
        <Route path={requestedReport} element={<AdminReports />} />
        <Route path={postedWorkshiftReport} element={<AdminReports />} />
        <Route
          path={ownershipReport}
          element={(
            <ProtectedCustomRoute route={bookingReport} allow={isSuperAdmin} />
          )}
        >
          <Route path={ownershipReport} element={<AdminReports />} />
        </Route>
        <Route path={pharmacistList} element={<PharmacistList />} />
        <Route path={techniciansList} element={<TechniciansList />} />
        <Route path={pharmacyStudentList} element={<PharmacyStudentList />} />
        <Route path={pharmacyList} element={<PharmacyList />} />
        <Route path={listOfWorkshifts} element={<ListOfWorkshifts />} />
        <Route path={adminConfiguration} element={<AdminConfiguration />} />
        <Route path={incompatibleMatch} element={<IncompatibleMatches />} />
        <Route path={createNewAdmin} element={<CreateNewAdmin />} />
        <Route path={updateAdmin} element={<UpdateAdmin />} />
        <Route path={assignAccounts} element={<AssignAccounts />} />
        <Route
          path={updateIncompatibleMatch}
          element={<UpdateIncompatibleMatches />}
        />
        <Route
          path={viewRatings}
          element={<DetailedRatings user={TypeOfUser.pharmacist} />}
        />
        <Route
          path={viewTechnicianRating}
          element={<DetailedRatings user={TypeOfUser.technician} />}
        />
        <Route
          path={viewStudentRating}
          element={<DetailedRatings user={TypeOfUser.student} />}
        />
        <Route
          path={pharmacyViewRatings}
          element={<DetailedRatings user={TypeOfUser.pharmacy} />}
        />
        <Route
          path={pharmacyListIncompatible}
          element={<IncompatibleMatches />}
        />
        <Route
          path={pharmacyListIncompatibleUpdate}
          element={<UpdateIncompatibleMatches />}
        />
        <Route
          path={broadcastList}
          element={<Navigate to={broadcastListNotification} />}
        />
        <Route path={broadcastList} element={<BroadcastList />}>
          <Route path={notifications} element={<BroadcastList />} />
          <Route path={groups} element={<BroadcastList />} />
        </Route>
        <Route
          path={createBroadcastGroup}
          element={
            <CreateEditBroadcastGroup type={BroadcastGroupActionType.create} />
          }
        />
        <Route
          path={editBroadcastGroup}
          element={
            <CreateEditBroadcastGroup type={BroadcastGroupActionType.edit} />
          }
        />
        <Route
          path={createNotification}
          element={(
            <CreateEditBroadcastNotification
              type={BroadcastGroupActionType.create}
            />
          )}
        />
        <Route
          path={editNotification}
          element={(
            <CreateEditBroadcastNotification
              type={BroadcastGroupActionType.edit}
            />
          )}
        />
        <Route path={logs} element={<Navigate replace to={activityLogs} />} />
        <Route path={activityLogs} element={<Logs />} />
        <Route path={workshiftLogs} element={<Logs />} />
        <Route path={availabilityLogs} element={<Logs />} />
        <Route path={bookingLogs} element={<Logs />} />
        <Route path={profile} element={<AdminProfile />} />
        <Route path={changePassword} element={<ChangePassword />} />
        <Route
          path={pharmacyAdministrators}
          element={<PharmacyAdministrators />}
        />
        <Route
          path={createPharmacyAdministrators}
          element={<CreateEditPharmacyAdministrator type={create} />}
        />
        <Route
          path={updatePharmacyAdministrators}
          element={<CreateEditPharmacyAdministrator type={edit} />}
        />
        <Route
          path={appointPharmacyAdministrators}
          element={<CreateEditPharmacyAdministrator type={appoint} />}
        />
        <Route
          path={appointPharmacyList}
          element={<AppointExistingPharmacy />}
        />
        <Route path={advertisingCampaigns} element={<AdvertisingCampaign />} />
        <Route
          path={createAdvertisingCampaign}
          element={(
            <CreateEditAdvertisingCampaign
              type={BroadcastGroupActionType.create}
            />
          )}
        />
        <Route
          path={editAdvertisingCampaign}
          element={(
            <CreateEditAdvertisingCampaign
              type={BroadcastGroupActionType.edit}
            />
          )}
        />
        <Route path={campaigns} element={<Campaigns />} />
        <Route
          path={createCampaign}
          element={
            <CampaignStepper type={BroadcastGroupActionType.create} />
          }
        />
        <Route
          path={editCampaign}
          element={<CampaignStepper type={BroadcastGroupActionType.edit} />}
        />
        {/* <Route
          path={configureCampaignEmail}
          element={<ConfigureCampaignEmail />}
        /> */}
        <Route
          path={campaignBookings}
          element={<CampaignBookings type={CampaignBookingType.campaign} />}
        />
        <Route
          path={leadBookings}
          element={<CampaignBookings type={CampaignBookingType.leads} />}
        />
        <Route path={linkedLeads} element={<Leads />} />
        <Route path={leads} element={<Navigate to={pharmacyLeads} />} />
        <Route path={pharmacistLeads} element={<Leads defaultTab={LeadTabs.pharmacist} />} />
        <Route path={technicianLeads} element={<Leads defaultTab={LeadTabs.technicians} />} />
        <Route path={pharmacyLeads} element={<Leads defaultTab={LeadTabs.pharmacy} />} />
        <Route path={studentLeads} element={<Leads defaultTab={LeadTabs.pharmacist} />} />

        <Route
          path={createLead}
          element={<CreateEditLead type={BroadcastGroupActionType.create} />}
        />
        <Route
          path={editLead}
          element={<CreateEditLead type={BroadcastGroupActionType.edit} />}
        />
        <Route path={entitlements} element={<Entitlements />} />
        <Route path={editEntitlement} element={<EditEntitlement />} />

        <Route
          path={adminChat}
          element={(
            <ProtectedCustomRoute
              route={Constants.paths.admin.adminCalendarDashboard}
              allow={isChatEnabled}
            />
          )}
        >
          <Route path={adminChat} element={<ChatWindow />} />
        </Route>
        <Route path={transactions} element={<PharmagapTransactions />} />

        <Route path={pendingRequests} element={<PendingRequestsList />} />
      </Route>
    </Route>
  );
};

export default AdminRouter;
